import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";

import { useMediaQuery } from "@mui/material";
import { StepperStep, stepperSteps } from "constants/stepperSteps";
import { ClientProduct } from "customTypes/ClientProducts";
import { updateStartPageSelection } from "store/features/clientSlice";
import { setSteps } from "store/features/stepperSlice";
import { clientSelector } from "store/selectors/selectors";
import { RootState } from "store/store";
import { useT } from "translation";
import { getProductTitle } from "utils/utils";
import { ProductIconMapping } from "../utils";
import ProductSelectionCard from "./ProductSelectionCard";

interface TabPanelProps {
  children?: React.ReactNode;
  value: string;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={!value}
      id={`simple-tabpanel-${value}`}
      aria-labelledby={`simple-tab-${value}`}
      {...other}
    >
      <Box sx={{ p: 2 }}>{children}</Box>
    </Box>
  );
}

export default function StartSectionTabPanel() {
  const isMobile = useMediaQuery("max-width:425px");
  const [value, setValue] = React.useState<ProductCategory>("BUSINESS");
  const dispatch = useDispatch();

  const productConfig = useSelector(
    (state: RootState) => state.client.productConfig
  );

  const [availableTabs, setAvailableTabs] = React.useState([]);

  const { t } = useT();
  const {
    availableProducts,
    startPageSelection,
  } = useSelector((state: RootState) => {
    return state.client;
  });

  const getSortedProducts = (products: ClientProduct[], category: string) => {
    if (!productConfig?.sequence || !products) return products;

    const categoryProducts = products.filter(
      (product) => product?.product?.product_category === category
    );

    const sequenceMap = new Map(
      productConfig.sequence.map((identifier, index) => [identifier, index])
    );

    return [...categoryProducts].sort((a, b) => {
      const aIndex =
        sequenceMap.get(a.product.product_identifier) ??
        Number.MAX_SAFE_INTEGER;
      const bIndex =
        sequenceMap.get(b.product.product_identifier) ??
        Number.MAX_SAFE_INTEGER;

      if (aIndex !== bIndex) return aIndex - bIndex;
      return a?.product?.ready_to_use ? -1 : 1;
    });
  };

  const handleChange = (
    event: React.SyntheticEvent,
    newValue: ProductCategory
  ) => {
    dispatch(updateStartPageSelection([]));
    setValue(newValue);
  };
  React.useEffect(() => {
    const tabSet = new Set<string>();
    availableProducts.forEach((p) => {
      tabSet.add(p?.product?.product_category);
    });
    const arr = Array.from(tabSet);
    setAvailableTabs(arr);
    setValue(arr[0] as ProductCategory);
  }, [availableProducts]);

  React.useEffect(() => {
    if (
      productConfig?.preSelectedProducts?.length &&
      availableProducts.length &&
      startPageSelection.length === 0
    ) {
      const productsToSelect = availableProducts.filter((product) =>
        productConfig.preSelectedProducts.includes(
          product.product.product_identifier.split("-")[0]
        )
      );

      if (productsToSelect.length) {
        dispatch(updateStartPageSelection(productsToSelect));
      }
    }
  }, [availableProducts, productConfig, dispatch, startPageSelection.length]);

  const RenderProductIcon = (p: ClientProduct) => {
    if (p.product.product_identifier === "CONDO_HOA") {
      return (
        <div style={{ height: "51px", width: "51px" }}>
          {ProductIconMapping(
            p.product.product_identifier,
            !p?.product?.ready_to_use
          )}
        </div>
      );
    }
    return ProductIconMapping(
      p.product.product_identifier,
      !p?.product?.ready_to_use
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box>
        <Tabs value={value} onChange={handleChange}>
          {availableTabs.map((tab) => (
            <Tab
              data-cy={`tab-${tab}`}
              key={tab}
              sx={{
                textTransform: "none",
              }}
              value={tab}
              label={t(`START_PAGE_TABS.${tab}`)}
            />
          ))}
        </Tabs>
      </Box>
      <TabPanel value={value}>
        <Box
          sx={{
            display: "flex",
            gap: isMobile ? "10px" : "20px",
            flexWrap: "wrap",
          }}
        >
          {getSortedProducts(availableProducts, value).map((p) => {
            return (
              <ProductSelectionCard
                key={p.product.id}
                id={p.product.id}
                product={p}
                prodIcon={RenderProductIcon(p)}
                isComingSoon={!p?.product?.ready_to_use}
                prodTitle={getProductTitle(p)}
              />
            );
          })}
        </Box>
      </TabPanel>
    </Box>
  );
}
